import { useCallback, useEffect, useState } from 'react';

import { useLocalStorage } from '@/hooks/use-local-storage';

const STORAGE_KEY = 'team_member_invite_cooldowns';

export const INVITE_COOLDOWN_MS = 60 * 60 * 1000; // 1 hour (Cognito limit)

type CooldownMap = Record<string, number>;

const EMPTY_MAP: CooldownMap = {};

const prune = (map: CooldownMap): CooldownMap => {
  const now = Date.now();
  const next: CooldownMap = {};
  for (const [id, ts] of Object.entries(map)) {
    if (typeof ts === 'number' && now - ts < INVITE_COOLDOWN_MS) {
      next[id] = ts;
    }
  }
  return next;
};

const getRemaining = (map: CooldownMap, id: string | undefined) => {
  if (!id) return 0;
  const ts = map[id];
  if (!ts) return 0;
  const remaining = INVITE_COOLDOWN_MS - (Date.now() - ts);
  return remaining > 0 ? remaining : 0;
};

/**
 * Tracks last invite/resend timestamp per team member in localStorage
 * to enforce Cognito's 1-hour cooldown on the client.
 *
 * Returns remaining cooldown in ms (0 when none), and helpers to record
 * a new send and to clear the cooldown.
 */
export function useInviteCooldown(teamMemberId: string | undefined) {
  const [map, setMap] = useLocalStorage<CooldownMap>(STORAGE_KEY, EMPTY_MAP);

  const [remainingMs, setRemainingMs] = useState<number>(() => getRemaining(map, teamMemberId));

  // Re-tick remaining time every second while a cooldown is active so the UI
  // (e.g. the cooldown modal's countdown) stays current without extra writes.
  useEffect(() => {
    setRemainingMs(getRemaining(map, teamMemberId));

    if (!teamMemberId || !map[teamMemberId]) return;

    const interval = window.setInterval(() => {
      setRemainingMs(getRemaining(map, teamMemberId));
    }, 1000);
    return () => window.clearInterval(interval);
  }, [map, teamMemberId]);

  const recordSend = useCallback(
    (id: string) => {
      setMap({ ...prune(map), [id]: Date.now() });
    },
    [map, setMap]
  );

  const clearCooldown = useCallback(
    (id: string) => {
      if (map[id] === undefined) return;
      const { [id]: _removed, ...rest } = map;
      setMap(rest);
    },
    [map, setMap]
  );

  return {
    remainingMs,
    isOnCooldown: remainingMs > 0,
    recordSend,
    clearCooldown,
  };
}
